import * as THREE from 'three';
import { CONFIG } from '../../config';
import type { GameState } from '../../state/game-state';

export class CorruptionOverlay {
  public root: THREE.Group;
  private domEl: HTMLDivElement | null = null;
  private respawnEl: HTMLDivElement | null = null;
  private warnEl: HTMLDivElement | null = null;
  private lastShakeOffset = new THREE.Vector3();
  private shakeBase = new THREE.Vector3(0, 2.4, 8.5);
  private lastWarnState: 'normal' | 'warn' | 'crit' = 'normal';

  constructor(parent: HTMLElement) {
    this.root = new THREE.Group();
    const el = document.createElement('div');
    el.id = 'corruption-overlay';
    el.style.cssText = `
      position: absolute; inset: 0; pointer-events: none; z-index: 5;
      background: radial-gradient(ellipse at center, transparent 30%, rgba(170, 48, 16, 0.0) 70%);
      transition: background 0.2s ease, opacity 0.4s ease;
      mix-blend-mode: multiply;
    `;
    parent.appendChild(el);
    this.domEl = el;

    const warn = document.createElement('div');
    warn.id = 'corruption-warn';
    warn.style.cssText = `
      position: absolute; left: 50%; top: 38%; transform: translateX(-50%);
      font-family: var(--font-mono); font-size: 18px; font-weight: 500;
      color: #ffcc44; letter-spacing: 0.3em; padding: 8px 24px;
      background: rgba(80, 20, 20, 0.0); border: 1px solid rgba(255, 170, 100, 0);
      border-radius: 8px; opacity: 0; transition: opacity 0.3s ease, color 0.3s ease, border-color 0.3s ease, background 0.3s ease;
      pointer-events: none; z-index: 6;
    `;
    warn.textContent = '';
    parent.appendChild(warn);
    this.warnEl = warn;

    const rs = document.createElement('div');
    rs.id = 'corruption-respawn';
    rs.style.cssText = `
      position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
      font-family: var(--font-cn); font-size: 28px; font-weight: 500;
      color: #fff8e8; letter-spacing: 0.3em; padding: 24px 48px;
      background: rgba(170, 48, 16, 0.0); border: 2px solid rgba(255, 200, 100, 0);
      border-radius: 12px; opacity: 0; transition: opacity 0.2s ease, background 0.3s ease, border-color 0.3s ease;
      pointer-events: none; z-index: 7; text-align: center;
    `;
    rs.innerHTML = '<div class="rs-title">精神体重生中</div><div class="rs-sub" style="font-size:14px;opacity:0.7;margin-top:8px;letter-spacing:0.1em;">—</div>';
    parent.appendChild(rs);
    this.respawnEl = rs;
  }

  update(now: number, state: GameState, _deltaMs: number, camera: THREE.PerspectiveCamera, orbPos: THREE.Vector3): void {
    const ratio = state.corruption / CONFIG.corruption.max;

    if (this.domEl) {
      if (state.isDead) {
        this.domEl.style.opacity = '0.95';
        this.domEl.style.background = `radial-gradient(ellipse at center, transparent 20%, rgba(120, 16, 8, 0.85) 70%, rgba(40, 4, 0, 0.95) 100%), repeating-linear-gradient(45deg, transparent 0 4px, rgba(255, 60, 30, 0.18) 4px 6px)`;
      } else if (ratio < 0.05) {
        this.domEl.style.opacity = '0';
      } else {
        const intensity = Math.min(1, ratio);
        const r = Math.round(170 + 85 * intensity);
        const g = Math.round(48 - 48 * intensity);
        const b = Math.round(16 - 16 * intensity);
        const pulse = ratio >= CONFIG.corruption.critThreshold
          ? 0.5 + 0.5 * Math.sin(now * 0.012)
          : 1;
        this.domEl.style.opacity = String(Math.min(1, 0.3 + intensity * 0.7) * pulse);
        this.domEl.style.background = `radial-gradient(ellipse at center, transparent ${30 - intensity * 20}%, rgba(${r}, ${g}, ${b}, ${0.4 + intensity * 0.5}) 100%)`;
        if (ratio >= 0.6) {
          this.domEl.style.background += `, repeating-linear-gradient(45deg, transparent 0 3px, rgba(${r}, ${g}, ${b}, 0.2) 3px 4px)`;
        }
      }
    }

    if (this.warnEl) {
      if (state.isDead || state.isRespawning) {
        this.warnEl.style.opacity = '0';
        this.lastWarnState = 'normal';
      } else if (ratio >= CONFIG.corruption.critThreshold) {
        this.warnEl.textContent = '⚠ 危险 ⚠';
        this.warnEl.style.opacity = String(0.5 + 0.5 * Math.sin(now * 0.015));
        this.warnEl.style.color = '#ff6644';
        this.warnEl.style.background = 'rgba(120, 20, 20, 0.45)';
        this.warnEl.style.borderColor = 'rgba(255, 100, 68, 0.7)';
        this.lastWarnState = 'crit';
      } else if (ratio >= CONFIG.corruption.warnThreshold) {
        this.warnEl.textContent = '⚠ 警告 ⚠';
        this.warnEl.style.opacity = String(0.4 + 0.4 * Math.sin(now * 0.005));
        this.warnEl.style.color = '#ffcc44';
        this.warnEl.style.background = 'rgba(80, 50, 10, 0.35)';
        this.warnEl.style.borderColor = 'rgba(255, 200, 100, 0.5)';
        this.lastWarnState = 'warn';
      } else {
        this.warnEl.style.opacity = '0';
        this.lastWarnState = 'normal';
      }
    }

    if (this.respawnEl) {
      if (state.isDead) {
        this.respawnEl.style.opacity = '0';
      } else if (state.isRespawning) {
        const t = Math.min(1, (now - state.respawnStartedAt) / CONFIG.corruption.respawnDurationMs);
        const remaining = Math.max(0, CONFIG.corruption.respawnDurationMs - (now - state.respawnStartedAt));
        const seconds = (remaining / 1000).toFixed(1);
        this.respawnEl.style.opacity = '1';
        this.respawnEl.style.background = 'rgba(80, 20, 20, 0.55)';
        this.respawnEl.style.borderColor = 'rgba(252, 200, 100, 0.7)';
        const title = this.respawnEl.querySelector('.rs-title') as HTMLDivElement;
        const sub = this.respawnEl.querySelector('.rs-sub') as HTMLDivElement;
        if (t < 0.3) {
          title.textContent = '被吞噬中…';
        } else if (t < 0.55) {
          title.textContent = '裂解中…';
        } else if (t < 0.85) {
          title.textContent = '重组中…';
        } else {
          title.textContent = '即将复活';
        }
        sub.textContent = `${seconds}s · 击中恐龙或张手加速净化`;
      } else {
        this.respawnEl.style.opacity = '0';
      }
    }

    camera.position.sub(this.lastShakeOffset);
    this.lastShakeOffset.set(0, 0, 0);

    let shakeAmp = 0;
    if (state.isRespawning) {
      shakeAmp = 0.15;
    } else if (ratio >= CONFIG.corruption.critThreshold) {
      shakeAmp = 0.12 + 0.08 * Math.sin(now * 0.02);
    } else if (ratio >= 0.6) {
      shakeAmp = 0.05 + (ratio - 0.6) * 0.3;
    }

    if (shakeAmp > 0) {
      const t = now * 0.02;
      this.lastShakeOffset.set(
        Math.sin(t) * shakeAmp,
        Math.cos(t * 1.3) * shakeAmp * 0.5,
        Math.sin(t * 0.7) * shakeAmp * 0.3
      );
      camera.position.add(this.lastShakeOffset);
    }

    camera.lookAt(orbPos.x, 1.2, orbPos.z);
  }

  getWarnState(): 'normal' | 'warn' | 'crit' {
    return this.lastWarnState;
  }
}
